import { ApiClient } from 'clientSDK/ApiClient';

/**
 * Singleton service for managing LP Bonds prices
 * Handles fetching, caching, and broadcasting price updates to subscribers
 */
class PriceService {
  constructor() {
    // Singleton state
    this.prices = null;
    this.loading = true;
    this.error = null;
    this.fetchPromise = null;
    this.intervalId = null;
    this.subscribers = [];
  }

  /**
   * Subscribe to price updates
   * @param {Function} callback - Called with {prices, loading, error} when prices update
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }

      // Clear interval only when no more subscribers
      if (this.subscribers.length === 0 && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
        this.fetchPromise = null;
      }
    };
  }

  /**
   * Notify all subscribers of state changes
   */
  notifySubscribers() {
    this.subscribers.forEach((callback) => {
      callback({
        prices: this.prices,
        loading: this.loading,
        error: this.error,
      });
    });
  }

  /**
   * Fetch prices from API
   */
  async fetchPrices() {
    try {
      const response = await ApiClient.lpBondsApiInstance.axiosInstance.get('/prices');
      this.prices = response.data;
      this.error = null;
    } catch (err) {
      console.error('Failed to fetch LP Bonds prices:', err);
      this.error = err.message;
      // Set fallback prices if API fails
      this.prices = {
        ethereum: 3000,
        energi: 1.5,
        gonnamakeit: 0.001,
        apecoin: 1.2,
        'hello-labs': 0,
        kima: 0,
        monad: 0,
        binancecoin: 600,
        arbitrum: 0,
      };
    } finally {
      this.loading = false;
      this.notifySubscribers();
    }
  }

  /**
   * Initialize price fetching (called on first subscription)
   */
  async initialize() {
    // Initial fetch
    await this.fetchPrices();

    // Set up periodic refresh (every 60 seconds) - only one global interval
    if (!this.intervalId) {
      this.intervalId = setInterval(() => this.fetchPrices(), 60000);
    }
  }

  /**
   * Start the service (called when first subscriber subscribes)
   */
  start() {
    if (!this.fetchPromise && this.prices === null) {
      this.fetchPromise = this.initialize();
    }
  }

  /**
   * Manually refetch prices
   */
  async refetch() {
    this.loading = true;
    this.notifySubscribers();
    return this.initialize();
  }

  /**
   * Get current state snapshot
   */
  getState() {
    return {
      prices: this.prices || {},
      loading: this.loading,
      error: this.error,
    };
  }

  /**
   * Get specific asset price
   * @param {string} asset - Asset API key (ethereum, apecoin, etc.)
   * @returns {number} Price in USD
   */
  getAssetPrice(asset) {
    return this.prices?.[asset] || 0;
  }

  /**
   * Get multiple asset prices at once
   * @param {string[]} assets - Array of asset API keys
   * @returns {Object} Object with asset prices
   */
  getMultipleAssetPrices(assets) {
    const result = {};
    assets.forEach((asset) => {
      result[asset] = this.prices?.[asset] || 0;
    });
    return result;
  }
}

// Export singleton instance
const priceService = new PriceService();
export default priceService;
